import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../../lib/api.js';
import { GAME_MODES, getGamemodeColor, getGamemodeLabel } from '../../lib/gamemode.js';
import { formatDuration, getVideoDurationFromFile, isAllowedVideoFile } from './adminUtils.js';
import ChallengeEditor from './ChallengeEditor.js';

interface LessonVideo {
  id: string;
  title: string;
  videoUrl: string;
  durationSeconds: number;
  orderIndex: number;
}

interface Lesson {
  id: string;
  title: string;
  description: string;
  notes: string | null;
  orderIndex: number;
  videos: LessonVideo[];
  challengeHtml?: string | null;
}

interface Course {
  id: string;
  title: string;
  description: string;
  gamemode: string;
  difficulty: string;
  lessons: Lesson[];
  thumbnail_url?: string | null;
}

export default function AdminCoursesTab() {
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [editingCourseId, setEditingCourseId] = useState<string | null>(null);
  const [editingLessonId, setEditingLessonId] = useState<string | null>(null);
  const [showAddCourse, setShowAddCourse] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);
  const [addLessonCourseId, setAddLessonCourseId] = useState<string | null>(null);
  const [uploadLessonId, setUploadLessonId] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragVideoId, setDragVideoId] = useState<string | null>(null);

  // Challenge editor state
  const [challengeLesson, setChallengeLesson] = useState<Lesson | null>(null);

  const [courseForm, setCourseForm] = useState({
    title: '',
    description: '',
    gamemode: 'sword',
    difficulty: 'beginner'
  });

  const [lessonForm, setLessonForm] = useState({ title: '', description: '', notes: '' });
  const [videoForm, setVideoForm] = useState({ title: '', file: null as File | null });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get<Course[]>('/api/admin/courses');
      setCourses(data);
      setSelectedCourse(prev => {
        if (!prev) return null;
        return data.find(c => c.id === prev.id) ?? null;
      });
    } catch (err) {
      console.error('Failed to load courses:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleExpand = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleAddCourse = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const form = new FormData();
      form.append('title', courseForm.title);
      form.append('description', courseForm.description);
      form.append('gamemode', courseForm.gamemode);
      form.append('difficulty', courseForm.difficulty);
      await api.post('/api/admin/courses', form);
      setCourseForm({ title: '', description: '', gamemode: 'sword', difficulty: 'beginner' });
      setShowAddCourse(false);
      await load();
    } catch {
      alert('Failed to add course');
    }
  };

  const handleUpdateCourse = async (courseId: string, patch: Partial<Course>) => {
    try {
      await api.patch(`/api/admin/courses/${courseId}`, patch);
      setEditingCourseId(null);
      await load();
    } catch {
      alert('Failed to update course');
    }
  };

  const handleDeleteCourse = async (courseId: string) => {
    if (!confirm('Delete this course and all lessons/videos?')) return;
    try {
      await api.delete(`/api/admin/courses/${courseId}`);
      await load();
    } catch {
      alert('Failed to delete course');
    }
  };

  const handleAddLesson = async (e: React.FormEvent, courseId: string) => {
    e.preventDefault();
    try {
      await api.post(`/api/admin/courses/${courseId}/lessons`, lessonForm);
      setLessonForm({ title: '', description: '', notes: '' });
      setAddLessonCourseId(null);
      setExpanded((prev) => new Set(prev).add(courseId));
      await load();
    } catch {
      alert('Failed to add lesson');
    }
  };

  const handleUpdateLesson = async (lessonId: string, patch: Partial<Lesson>) => {
    try {
      await api.patch(`/api/admin/lessons/${lessonId}`, patch);
      setEditingLessonId(null);
      await load();
    } catch {
      alert('Failed to update lesson');
    }
  };

  const handleDeleteLesson = async (lessonId: string) => {
    if (!confirm('Delete this lesson and all videos?')) return;
    try {
      await api.delete(`/api/admin/lessons/${lessonId}`);
      await load();
    } catch {
      alert('Failed to delete lesson');
    }
  };

  const handleUploadVideo = async (e: React.FormEvent, lessonId: string) => {
    e.preventDefault();
    if (!videoForm.title.trim() || !videoForm.file) return;
    if (!isAllowedVideoFile(videoForm.file)) {
      alert('Video must be .mp4, .webm, or .mov');
      return;
    }
    setUploadProgress(0);
    try {
      const duration = await getVideoDurationFromFile(videoForm.file);
      const formData = new FormData();
      formData.append('video', videoForm.file);
      formData.append('title', videoForm.title.trim());
      formData.append('duration_seconds', String(duration));
      await api.post(`/api/lessons/${lessonId}/videos`, formData, {
        onUploadProgress: (ev) => {
          if (ev.total) setUploadProgress(Math.round((ev.loaded / ev.total) * 100));
        }
      });
      setVideoForm({ title: '', file: null });
      setUploadLessonId(null);
      setUploadProgress(0);
      await load();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Upload failed';
      alert(msg);
    } finally {
      setUploadProgress(0);
    }
  };

  const handleDeleteVideo = async (lessonId: string, videoId: string) => {
    if (!confirm('Delete this video from storage?')) return;
    try {
      await api.delete(`/api/lessons/${lessonId}/videos/${videoId}`);
      await load();
    } catch {
      alert('Failed to delete video');
    }
  };

  const handleVideoReorder = async (lessonId: string, ordered: LessonVideo[]) => {
    try {
      await api.patch(`/api/lessons/${lessonId}/videos/reorder`, {
        items: ordered.map((v, i) => ({ id: v.id, order_index: i }))
      });
      await load();
    } catch {
      alert('Failed to reorder videos');
    }
  };

  const onVideoDrop = (lesson: Lesson, targetId: string) => {
    if (!dragVideoId || dragVideoId === targetId) return;
    const sorted = [...lesson.videos].sort((a, b) => a.orderIndex - b.orderIndex);
    const fromIdx = sorted.findIndex((v) => v.id === dragVideoId);
    const toIdx = sorted.findIndex((v) => v.id === targetId);
    if (fromIdx < 0 || toIdx < 0) return;
    const next = [...sorted];
    const [moved] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, moved);
    setDragVideoId(null);
    void handleVideoReorder(lesson.id, next);
  };

  // ── Challenge editor takes over the whole screen ──
  if (challengeLesson) {
    return (
      <ChallengeEditor
        lessonId={challengeLesson.id}
        lessonTitle={challengeLesson.title}
        onClose={() => {
          setChallengeLesson(null);
          void load();
        }}
      />
    );
  }

  if (loading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="h-20 bg-surface-variant rounded-lg animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <button
        type="button"
        onClick={() => setShowAddCourse(!showAddCourse)}
        className="px-4 py-2 bg-error text-white rounded font-button text-sm font-bold uppercase tracking-wider active:scale-[0.97]"
      >
        {showAddCourse ? 'Cancel' : '+ Add course'}
      </button>

      {showAddCourse && (
        <form onSubmit={handleAddCourse} className="bg-[#141414] thin-border rounded-lg p-4 space-y-3">
          <input
            placeholder="Title"
            value={courseForm.title}
            onChange={(e) => setCourseForm({ ...courseForm, title: e.target.value })}
            className="w-full px-3 py-2 bg-surface-variant border border-outline-variant rounded text-on-surface"
            required
          />
          <textarea
            placeholder="Description"
            value={courseForm.description}
            onChange={(e) => setCourseForm({ ...courseForm, description: e.target.value })}
            className="w-full px-3 py-2 bg-surface-variant border border-outline-variant rounded text-on-surface min-h-[80px]"
            required
          />
          <div className="flex gap-3">
            <select
              value={courseForm.gamemode}
              onChange={(e) => setCourseForm({ ...courseForm, gamemode: e.target.value })}
              className="flex-1 px-3 py-2 bg-surface-variant border border-outline-variant rounded text-on-surface"
            >
              {GAME_MODES.map((g) => (
                <option key={g} value={g}>{g}</option>
              ))}
            </select>
            <select
              value={courseForm.difficulty}
              onChange={(e) => setCourseForm({ ...courseForm, difficulty: e.target.value })}
              className="flex-1 px-3 py-2 bg-surface-variant border border-outline-variant rounded text-on-surface"
            >
              <option value="beginner">Beginner</option>
              <option value="advanced">Advanced</option>
            </select>
          </div>
          <button type="submit" className="px-4 py-2 bg-error text-white rounded text-sm font-bold">
            Save course
          </button>
        </form>
      )}

      {selectedCourse ? (
        <div className="space-y-4">
          <button
            type="button"
            onClick={() => setSelectedCourse(null)}
            className="text-sm text-on-surface-variant hover:text-on-surface"
          >
            ← Back to Courses
          </button>
          <div className="bg-[#141414] thin-border rounded-lg p-4">
            <div className="flex gap-4">
              <div className="flex-1">
                <h2 className="text-xl font-semibold text-on-surface">{selectedCourse.title}</h2>
                <div className="mt-3 p-4 bg-[#0f1112] rounded-lg thin-border">
                  <div className="text-sm text-on-surface-variant">{selectedCourse.description}</div>
                </div>
                <div className="mt-2 flex gap-2 text-xs items-center">
                  <div className={`px-2 py-1 rounded font-medium ${getGamemodeColor(selectedCourse.gamemode)}`}>
                    {getGamemodeLabel(selectedCourse.gamemode)}
                  </div>
                  <div className={`px-2 py-1 rounded font-medium ${
                    selectedCourse.difficulty === 'advanced'
                      ? 'bg-[#3b0f0f] text-red-400'
                      : 'bg-[#0f1b0f] text-green-400'
                  }`}>
                    {selectedCourse.difficulty}
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-4">
              <h3 className="text-sm font-medium text-on-surface mb-2">Lessons</h3>
              {selectedCourse.lessons.length === 0 && (
                <div className="text-on-surface-variant text-sm">No lessons yet — add one below.</div>
              )}
              <ol className="mt-2 space-y-2">
                {selectedCourse.lessons
                  .slice()
                  .sort((a, b) => a.orderIndex - b.orderIndex)
                  .map((lesson) => (
                    <li key={lesson.id} className="bg-surface-variant p-3 rounded">
                      <div className="flex justify-between items-start gap-2">
                        <div className="min-w-0">
                          <div className="font-medium text-on-surface flex items-center gap-2 flex-wrap">
                            <span>{lesson.title}</span>
                            {lesson.challengeHtml && (
                              <span className="text-[10px] px-1.5 py-0.5 bg-[rgba(229,57,53,0.15)] text-[#e53935] rounded font-semibold uppercase tracking-wide shrink-0">
                                Challenge
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-on-surface-variant mt-0.5">
                            {lesson.videos.length} video{lesson.videos.length !== 1 ? 's' : ''}
                          </div>
                        </div>

                        {/* Action buttons */}
                        <div className="flex gap-1.5 flex-wrap justify-end shrink-0">
                          <button
                            className="text-xs px-2 py-1 border border-outline-variant rounded text-on-surface-variant hover:text-on-surface transition-colors"
                            onClick={() => setEditingLessonId(editingLessonId === lesson.id ? null : lesson.id)}
                          >
                            {editingLessonId === lesson.id ? 'Cancel' : 'Edit'}
                          </button>
                          <button
                            className={`text-xs px-2 py-1 border rounded transition-colors ${
                              lesson.challengeHtml
                                ? 'border-[#e53935]/40 text-[#e53935] hover:bg-[rgba(229,57,53,0.08)]'
                                : 'border-outline-variant text-on-surface-variant hover:text-on-surface hover:border-[#e53935]/40'
                            }`}
                            onClick={() => setChallengeLesson(lesson)}
                          >
                            {lesson.challengeHtml ? '✎ Challenge' : '+ Challenge'}
                          </button>
                          <button
                            className="text-xs px-2 py-1 border border-outline-variant rounded text-red-400 hover:text-red-300 transition-colors"
                            onClick={() => handleDeleteLesson(lesson.id)}
                          >
                            Delete
                          </button>
                        </div>
                      </div>

                      {editingLessonId === lesson.id && (
                        <LessonEditForm
                          lesson={lesson}
                          onSave={(patch) => handleUpdateLesson(lesson.id, patch)}
                          onCancel={() => setEditingLessonId(null)}
                        />
                      )}

                      {/* Videos list */}
                      <div className="mt-3 space-y-1">
                        {lesson.videos
                          .slice()
                          .sort((a, b) => a.orderIndex - b.orderIndex)
                          .map((v) => (
                            <div
                              key={v.id}
                              className="flex items-center justify-between bg-[#0f1112] px-3 py-2 rounded text-sm"
                              draggable
                              onDragStart={() => setDragVideoId(v.id)}
                              onDragOver={(e) => e.preventDefault()}
                              onDrop={() => onVideoDrop(lesson, v.id)}
                            >
                              <div className="flex items-center gap-2">
                                <span className="text-on-surface-variant cursor-grab">⠿</span>
                                <span className="text-on-surface">{v.title}</span>
                              </div>
                              <div className="flex items-center gap-3">
                                <span className="text-xs text-on-surface-variant font-mono">
                                  {formatDuration(v.durationSeconds)}
                                </span>
                                <button
                                  onClick={() => handleDeleteVideo(lesson.id, v.id)}
                                  className="text-xs text-red-400 hover:text-red-300"
                                >
                                  Delete
                                </button>
                              </div>
                            </div>
                          ))}
                      </div>

                      {/* Upload progress */}
                      {uploadLessonId === lesson.id && uploadProgress > 0 && (
                        <div className="mt-2">
                          <div className="h-1.5 bg-outline-variant rounded overflow-hidden">
                            <div
                              className="h-1.5 bg-error rounded transition-all duration-300"
                              style={{ width: `${uploadProgress}%` }}
                            />
                          </div>
                          <div className="text-xs text-on-surface-variant mt-1">{uploadProgress}% uploaded</div>
                        </div>
                      )}

                      {/* Upload video */}
                      <div className="mt-3 flex gap-2 flex-wrap">
                        <button
                          type="button"
                          onClick={() => setUploadLessonId(uploadLessonId === lesson.id ? null : lesson.id)}
                          className="text-xs px-2 py-1 border border-outline-variant rounded text-on-surface-variant hover:text-on-surface"
                        >
                          {uploadLessonId === lesson.id ? 'Cancel Upload' : '+ Upload video'}
                        </button>
                        {uploadLessonId === lesson.id && (
                          <form
                            onSubmit={(e) => handleUploadVideo(e, lesson.id)}
                            className="flex gap-2 items-center flex-wrap"
                          >
                            <input
                              value={videoForm.title}
                              onChange={(e) => setVideoForm({ ...videoForm, title: e.target.value })}
                              placeholder="Video title"
                              className="px-2 py-1 bg-surface-variant border border-outline-variant rounded text-on-surface text-sm"
                              required
                            />
                            <input
                              type="file"
                              accept="video/mp4,video/webm,video/quicktime"
                              onChange={(e) => setVideoForm({ ...videoForm, file: e.target.files?.[0] ?? null })}
                              className="text-xs text-on-surface-variant"
                              required
                            />
                            <button
                              type="submit"
                              className="text-xs px-3 py-1.5 bg-error text-white rounded font-bold"
                            >
                              Upload
                            </button>
                          </form>
                        )}
                      </div>
                    </li>
                  ))}
              </ol>

              {/* Add lesson form */}
              <div className="mt-3">
                {addLessonCourseId === selectedCourse.id ? (
                  <form onSubmit={(e) => handleAddLesson(e, selectedCourse.id)} className="space-y-2 bg-[#0f1112] p-3 rounded">
                    <input
                      value={lessonForm.title}
                      onChange={(e) => setLessonForm({ ...lessonForm, title: e.target.value })}
                      placeholder="Lesson title"
                      className="w-full px-2 py-1.5 bg-surface-variant border border-outline-variant rounded text-on-surface text-sm"
                      required
                    />
                    <textarea
                      value={lessonForm.description}
                      onChange={(e) => setLessonForm({ ...lessonForm, description: e.target.value })}
                      placeholder="Lesson description"
                      className="w-full px-2 py-1.5 bg-surface-variant border border-outline-variant rounded text-on-surface text-sm"
                      required
                    />
                    <textarea
                      value={lessonForm.notes}
                      onChange={(e) => setLessonForm({ ...lessonForm, notes: e.target.value })}
                      placeholder="Notes (optional)"
                      className="w-full px-2 py-1.5 bg-surface-variant border border-outline-variant rounded text-on-surface text-sm"
                    />
                    <div className="flex gap-2">
                      <button type="submit" className="px-3 py-1.5 bg-error text-white rounded text-sm font-bold">
                        Save lesson
                      </button>
                      <button
                        type="button"
                        onClick={() => setAddLessonCourseId(null)}
                        className="px-3 py-1.5 border border-outline-variant rounded text-sm text-on-surface-variant"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                ) : (
                  <button
                    onClick={() => setAddLessonCourseId(selectedCourse.id)}
                    className="px-3 py-1.5 border border-outline-variant rounded text-sm text-on-surface-variant hover:text-on-surface"
                  >
                    + Add lesson
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : (
        // Course grid
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {courses.map((course) => (
            <React.Fragment key={course.id}>
              <div className="bg-[#141414] thin-border rounded-lg overflow-hidden">
                <div className="h-24 bg-[#151515] flex items-center justify-center relative">
                  <span className="text-3xl">
                    {course.gamemode === 'sword' ? '⚔️'
                      : course.gamemode === 'mace' ? '🔨'
                      : course.gamemode === 'mace_rockets' ? '🚀'
                      : course.gamemode === 'cpvp' ? '💎'
                      : course.gamemode === 'axe_shield' ? '🛡️'
                      : course.gamemode === 'nethpot' ? '🧪'
                      : course.gamemode === 'tank' ? '🪖'
                      : '🎮'}
                  </span>
                  <div className={`absolute top-2 left-2 px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
                    course.difficulty === 'beginner'
                      ? 'bg-[rgba(76,175,80,0.15)] text-[#4caf50]'
                      : 'bg-[rgba(255,191,0,0.15)] text-[#ffbf00]'
                  }`}>
                    {course.difficulty}
                  </div>
                </div>
                <div className="p-4">
                  <div className="font-semibold text-on-surface truncate">{course.title}</div>
                  <div className="text-xs mt-1 flex gap-2 items-center">
                    <div className={`px-2 py-0.5 rounded text-xs font-medium ${getGamemodeColor(course.gamemode)}`}>
                      {getGamemodeLabel(course.gamemode)}
                    </div>
                  </div>
                  <div className="mt-1 text-xs text-on-surface-variant">
                    {course.lessons.length} lesson{course.lessons.length !== 1 ? 's' : ''} · {course.lessons.reduce((acc, l) => acc + (l.videos?.length || 0), 0)} videos
                  </div>
                  <div className="mt-3 flex items-center gap-2">
                    <button
                      onClick={() => setSelectedCourse(course)}
                      className="text-xs px-3 py-1.5 bg-error text-white rounded font-bold"
                    >
                      Open
                    </button>
                    <button
                      onClick={() => setEditingCourseId(editingCourseId === course.id ? null : course.id)}
                      className="text-xs px-2 py-1.5 border border-outline-variant rounded text-on-surface-variant hover:text-on-surface"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteCourse(course.id)}
                      className="text-xs px-2 py-1.5 border border-outline-variant rounded text-red-400 hover:text-red-300"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>

              {editingCourseId === course.id && (
                <CourseEditForm
                  course={course}
                  onSave={(patch) => handleUpdateCourse(course.id, patch)}
                  onCancel={() => setEditingCourseId(null)}
                />
              )}
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
}

function CourseEditForm({
  course,
  onSave,
  onCancel,
}: {
  course: Course;
  onSave: (patch: Partial<Course>) => void;
  onCancel: () => void;
}) {
  const [title, setTitle] = useState(course.title);
  const [description, setDescription] = useState(course.description);
  const [gamemode, setGamemode] = useState(course.gamemode);
  const [difficulty, setDifficulty] = useState(course.difficulty);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSave({ title, description, gamemode, difficulty });
      }}
      className="p-4 border-t border-outline-variant/30 space-y-2 bg-surface-container"
    >
      <input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        className="w-full px-3 py-2 bg-surface-variant rounded text-on-surface text-sm"
      />
      <textarea
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        className="w-full px-3 py-2 bg-surface-variant rounded text-on-surface text-sm min-h-[60px]"
      />
      <div className="flex gap-2">
        <select
          value={gamemode}
          onChange={(e) => setGamemode(e.target.value)}
          className="flex-1 px-2 py-1.5 bg-surface-variant rounded text-sm text-on-surface"
        >
          {GAME_MODES.map((g) => (
            <option key={g} value={g}>{g}</option>
          ))}
        </select>
        <select
          value={difficulty}
          onChange={(e) => setDifficulty(e.target.value)}
          className="flex-1 px-2 py-1.5 bg-surface-variant rounded text-sm text-on-surface"
        >
          <option value="beginner">Beginner</option>
          <option value="advanced">Advanced</option>
        </select>
      </div>
      <div className="flex gap-2">
        <button type="submit" className="text-xs px-3 py-1.5 bg-error text-white rounded font-bold">Save</button>
        <button type="button" onClick={onCancel} className="text-xs px-3 py-1.5 border border-outline-variant rounded text-on-surface-variant">Cancel</button>
      </div>
    </form>
  );
}

function LessonEditForm({
  lesson,
  onSave,
  onCancel,
}: {
  lesson: Lesson;
  onSave: (patch: Partial<Lesson>) => void;
  onCancel: () => void;
}) {
  const [title, setTitle] = useState(lesson.title);
  const [description, setDescription] = useState(lesson.description);
  const [notes, setNotes] = useState(lesson.notes ?? '');

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSave({ title, description, notes });
      }}
      className="mt-3 p-3 border border-outline-variant/40 rounded space-y-2 bg-[#0f1112]"
    >
      <input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        className="w-full px-2 py-1.5 bg-surface-variant border border-outline-variant rounded text-sm text-on-surface"
      />
      <textarea
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        className="w-full px-2 py-1.5 bg-surface-variant border border-outline-variant rounded text-sm text-on-surface"
      />
      <textarea
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="Notes (optional)"
        className="w-full px-2 py-1.5 bg-surface-variant border border-outline-variant rounded text-sm text-on-surface"
      />
      <div className="flex gap-2">
        <button type="submit" className="text-xs px-2 py-1.5 bg-error text-white rounded font-bold">Save</button>
        <button type="button" onClick={onCancel} className="text-xs px-2 py-1.5 border border-outline-variant rounded text-on-surface-variant">Cancel</button>
      </div>
    </form>
  );
}